import React from 'react';
import { useQuery, gql } from '@apollo/client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import cx from 'classnames';
import { motion } from 'framer-motion';
import { Element, scroller } from 'react-scroll';

import useBreakpoint from 'hooks/useBreakpoint';
import withApollo from 'components/withApollo';
import PageLayout from 'components/PageLayout';
import Hero from 'components/Hero';
import Flex from 'components/Flex';
import SubnavLink from 'components/SubnavLink';
import MenuSection from 'components/MenuSection';
import Link from 'components/Link';
import MenuItemSymbol from 'components/MenuItemSymbol';
import CircleImage from 'components/CircleImage';

const QUERY = gql`
  query GetMenu($menu: MenuSectionMenuType!) {
    allMenuPages {
      id
      leadIn
      title
      heroImage {
        url: publicUrlTransformed(
          transformation: {
            width: "1440"
            height: "720"
            crop: "fill"
            gravity: "faces:auto"
            format: "auto"
            quality: "auto"
          }
        )
      }
      heroImageAlt {
        url: publicUrlTransformed(
          transformation: {
            width: "1440"
            height: "720"
            crop: "fill"
            gravity: "faces:auto"
            format: "auto"
            quality: "auto"
          }
        )
      }
      heroImageBrunch {
        url: publicUrlTransformed(
          transformation: {
            width: "1440"
            height: "720"
            crop: "fill"
            gravity: "faces:auto"
            format: "auto"
            quality: "auto"
          }
        )
      }

      dinnerMenu {
        url: publicUrl
      }
      drinksMenu {
        url: publicUrl
      }
      brunchMenu {
        url: publicUrl
      }

      disclaimer
      disclaimerImage {
        url: publicUrlTransformed(
          transformation: {
            width: "800"
            height: "800"
            crop: "fill"
            gravity: "faces:auto"
            format: "auto"
            quality: "auto"
          }
        )
      }
    }

    allMenuSections(where: { menu: $menu }, orderBy: "order") {
      id
      name
      description
      disclaimer
      isFeatured

      image {
        urlLandscape: publicUrlTransformed(
          transformation: {
            width: "1200"
            aspect_ratio: "8:3"
            crop: "fill"
            gravity: "faces:auto"
            format: "auto"
            quality: "auto"
          }
        )
        urlSquare: publicUrlTransformed(
          transformation: {
            width: "800"
            height: "800"
            crop: "fill"
            gravity: "faces:auto"
            format: "auto"
            quality: "auto"
          }
        )
      }

      items {
        id
        name
        description
        options
        price
        isGlutenFree
        isVegetarian
      }
    }
  }
`;

export default withApollo(() => {
  const mobile = !useBreakpoint('md');
  const [menu, setMenu] = React.useState('food');
  const [jumpNavOpen, setJumpNavOpen] = React.useState(false);

  const { data, loading } = useQuery(QUERY, {
    variables: {
      menu: { food: 'dinner', drinks: 'drinks', brunch: 'brunch' }[menu],
    },
  });
  const sections = data?.allMenuSections ?? [];
  const page = data?.allMenuPages?.[0];

  const { heroBg, menuUrl } = React.useMemo(() => {
    switch (menu) {
      case 'food': {
        return {
          heroBg: page?.heroImage?.url,
          menuUrl: page?.dinnerMenu?.url,
        };
      }

      case 'drinks': {
        return {
          heroBg: page?.heroImageAlt?.url,
          menuUrl: page?.drinksMenu?.url,
        };
      }

      case 'brunch': {
        return {
          heroBg: page?.heroImageBrunch?.url,
          menuUrl: page?.brunchMenu?.url,
        };
      }

      default: {
        console.error(`Unknown menu: ${menu}`);
        return {};
      }
    }
  }, [menu, page]);

  if (!page) return null;

  return (
    <PageLayout title={page.title}>
      <Hero leadIn={page.leadIn} title={page.title} image={heroBg}>
        <div className="mt-8 lg:mt-12">
          <div className="flex flex-col sm:flex-row sm:justify-center gap-y-2 gap-x-8">
            <SubnavLink
              component="button"
              type="button"
              label="Lunch/Dinner"
              active={menu === 'food'}
              onClick={() => setMenu('food')}
            />
            <SubnavLink
              component="button"
              type="button"
              label="Drinks"
              active={menu === 'drinks'}
              onClick={() => setMenu('drinks')}
            />
            <SubnavLink
              component="button"
              type="button"
              label="Brunch"
              active={menu === 'brunch'}
              onClick={() => setMenu('brunch')}
            />
          </div>
        </div>
      </Hero>

      {sections.length === 0 && !loading && (
        <p className="text-lg text-center">Sorry, menu currently unavailable</p>
      )}

      {sections.length > 0 && (
        <>
          <nav className="fixed z-20 bottom-0 inset-x-0">
            <div className={cx({ container: !mobile })}>
              <div className="max-w-md border-t-4 border-accent bg-gray-darker shadow-xl">
                <button
                  type="button"
                  className="uppercase text-white md:text-lg tracking-wider py-6 px-8 w-full text-left group flex items-center justify-between"
                  onClick={() => setJumpNavOpen((prev) => !prev)}
                >
                  Jump to Menu Section
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    size="lg"
                    className="ml-4 text-accent group-hover:text-white transition-colors duration-200"
                  />
                </button>
                {jumpNavOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="pb-6"
                  >
                    {sections.map((section) => (
                      <button
                        key={section.id}
                        type="button"
                        className="uppercase text-gray-light hover:text-white transition-colors duration-200 md:text-lg tracking-wider py-3 px-8 block w-full text-left"
                        onClick={() => {
                          scroller.scrollTo(section.id, {
                            duration: 1200,
                            delay: 50,
                            smooth: true,
                            offset: -100,
                          });

                          setJumpNavOpen(false);
                        }}
                      >
                        {section.name}
                      </button>
                    ))}
                  </motion.div>
                )}
              </div>
            </div>
          </nav>

          <div>
            {sections.map((section) => (
              <Element key={section.id} name={section.id}>
                <MenuSection {...section} className="my-16 first:mt-8" />
              </Element>
            ))}

            {menuUrl && (
              <div
                className={cx('mt-16 lg:mt-24 text-center text-lg', {
                  'mb-16 lg:mb-24': menu !== 'food',
                })}
              >
                <Link
                  href={menuUrl}
                  className="text-accent hover:text-accent-light transition-colors duration-200"
                  external
                >
                  Download full menu PDF
                  <FontAwesomeIcon icon={faChevronRight} size="1x" className="ml-3" />
                </Link>
              </div>
            )}

            {menu !== 'brunch' && (
              <div className="py-24">
                <div className="container">
                  <div className="flex justify-end">
                    {page.disclaimerImage && (
                      <div className="hidden lg:block w-1/4 relative">
                        <CircleImage image={page.disclaimerImage.url} id="menuDisclaimer" />
                      </div>
                    )}
                    <div
                      className={cx('w-full bg-gray-darker py-12 px-16', {
                        'lg:w-3/4': !!page.disclaimerImage,
                      })}
                    >
                      <Flex container="-ml-12 justify-center lg:justify-start" item="pl-12">
                        <p className="uppercase flex items-center text-sm text-white tracking-wide">
                          <MenuItemSymbol symbol="GF" theme="light" className="mr-3" />
                          Gluten-Friendly
                        </p>
                        <p className="uppercase flex items-center text-sm text-white tracking-wide">
                          <MenuItemSymbol symbol="V" theme="light" className="mr-3" />
                          Vegetarian
                        </p>
                      </Flex>
                      {page.disclaimer && (
                        <p className="text-sm leading-relaxed mt-6">{page.disclaimer}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </PageLayout>
  );
});
